import React, { useEffect, useState } from "react";
import { Modal, Table, Button, Form, Input, Space, Switch, Select, message, Segmented } from "antd";
import { EmailSettingAPI } from "../../constants/GenericAPI";

const MailTokenModal = ({ open, onCancel }) => {
  const [segmentedValue, setSegmentedValue] = useState(null); // 新增 state
  const [loading, setLoading] = useState(false);
  const [mailTokens, setMailTokens] = useState([]);
  const [editMailToken, setEditMailToken] = useState(null);
  const [form] = Form.useForm();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      EmailSettingAPI.getAll()
        .then(({ data }) => {
          console.log(data);
          setMailTokens(data.data);
          const hasActiveToken = mailTokens.some((token) => token.is_active === 1);
          setSegmentedValue(hasActiveToken ? "active" : "inactive");
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open]);

  const IMAP_OPTIONS = [
    { value: "imap.gmail.com", label: "Gmail" },
    { value: "outlook.office365.com", label: "Outlook.Office365" },
    { value: "imap.mail.yahoo.com", label: "Yahoo" },
  ];

  const getImapLabel = (value) => {
    const option = IMAP_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  // 新增處理狀態變更的函數
  const handleStatusChange = async (checked, record) => {
    try {
      // 呼叫 API 更新狀態
      await EmailSettingAPI.updateStatus(record.id, checked);
      // 更新本地資料
      setMailTokens((prevData) => prevData.map((item) => (item.id === record.id ? { ...item, is_active: checked ? 1 : 0 } : item)));
      message.success(`已${checked ? "啟用" : "停用"}該群組`);
    } catch (error) {
      message.error("更新狀態失敗");
      console.error("更新狀態失敗:", error);
    }
  };

  // 表格欄位定義
  const columns = [
    { title: "序號", dataIndex: "id", key: "id", align: "center", width: 80, sorter: (a, b) => a.id - b.id },
    { title: "電子信箱", dataIndex: "email", key: "email", align: "center" },
    { title: "應用程式專用密碼", dataIndex: "token", key: "token", align: "center" },
    {
      title: "IMAP 伺服器",
      dataIndex: "imap",
      key: "server",
      align: "center",
      width: 150,
      render: (server) => getImapLabel(server), // 使用映射函數來顯示標籤
    },
    {
      title: "啟用狀態",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (is_active, record) => (
        <Switch
          checked={is_active === 1}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          onChange={(checked) => handleStatusChange(checked, record)}
          size="small"
        />
      ),
    },
    {
      title: "動作",
      align: "center",
      render: (record) => (
        <Space>
          <Button
            onClick={() => {
              setEditMailToken(record);
              form.setFieldsValue({
                email: record.email,
                token: record.token,
                imap: record.imap,
                is_active: record.is_active,
              });
              setIsFormModalOpen(true);
            }}
          >
            編輯
          </Button>
          <Button onClick={() => handleDelete(record.id)}>刪除</Button>
        </Space>
      ),
    },
  ];

  // 處理刪除
  const handleDelete = (id) => {
    EmailSettingAPI.delete(id)
      .then(() => {
        setMailTokens(mailTokens.filter((token) => token.id !== id));
        message.success("刪除成功");
      })
      .catch(() => message.error("刪除失敗"));
  };

  // 處理表單提交
  const handleSubmit = (values) => {
    const data = {
      email: values.email,
      token: values.token,
      imap: values.imap,
      is_active: values.is_active ? 1 : 0,
    };
    console.log(data);

    if (editMailToken) {
      data.id = editMailToken.id;
      EmailSettingAPI.update(editMailToken.id, data)
        .then((response) => {
          // 更新現有記錄
          setMailTokens(
            mailTokens.map((record) => {
              if (record.id === editMailToken.id) {
                return { ...data, id: editMailToken.id };
              }
              return record;
            })
          );

          message.success("更新成功");
          resetForm();
          setIsFormModalOpen(false);
        })
        .catch(() => message.error("操作失敗"));
    } else {
      EmailSettingAPI.create(data)
        .then((response) => {
          console.log(response.data);
          setMailTokens([{ ...data, id: response.data.data.id }, ...mailTokens]);
          message.success("新增成功");
          resetForm();
          setIsFormModalOpen(false);
        })
        .catch(() => message.error("操作失敗"));
    }
  };

  // 重置表單
  const resetForm = () => {
    form.resetFields();
    setEditMailToken(null);
  };

  const handleBatchStatusUpdate = async (status) => {
    try {
      // 取得所有需要更新的 ID
      const tokensToUpdate = mailTokens.filter((token) => (status === "active" && token.is_active === 0) || (status === "inactive" && token.is_active === 1));

      if (tokensToUpdate.length === 0) {
        message.info("沒有需要更新的信箱");
        return;
      }
      setLoading(true);
      // 只更新需要改變的信箱
      const ids = tokensToUpdate.map((token) => token.id);
      await Promise.all(ids.map((id) => EmailSettingAPI.updateStatus(id, status === "active")));

      // 更新本地狀態
      setMailTokens((prevTokens) =>
        prevTokens.map((token) => {
          // 只更新需要改變的信箱狀態
          if (ids.includes(token.id)) {
            return {
              ...token,
              is_active: status === "active" ? 1 : 0,
            };
          }
          return token;
        })
      );
      setSegmentedValue(status);
      message.success(`已將所有信箱${status === "active" ? "啟用" : "停用"}`);
    } catch (error) {
      message.error("批量更新狀態失敗");
      console.error("批量更新狀態失敗:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="管理信箱"
        open={open}
        onCancel={onCancel}
        width={1100}
        footer={[
          <Button key="back" onClick={onCancel}>
            返回
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button
            type="primary"
            onClick={() => {
              resetForm();
              setIsFormModalOpen(true);
            }}
          >
            新增信箱
          </Button>

          <Space>
            <Button onClick={() => handleBatchStatusUpdate("active")} disabled={loading}>
              全部啟用
            </Button>
            <Button danger onClick={() => handleBatchStatusUpdate("inactive")} disabled={loading}>
              全部停用
            </Button>
          </Space>
        </div>
        <Table
          loading={loading}
          rowKey="id"
          bordered
          dataSource={mailTokens}
          columns={columns}
          pagination={{
            defaultPageSize: 10,
            showTotal: (total) => `共 ${total} 筆資料`,
          }}
          scroll={{ x: "max-content", y: 480 }}
        />
      </Modal>
      {/* 表單 Modal */}
      <Modal
        title={editMailToken ? "編輯信箱" : "新增信箱"}
        open={isFormModalOpen}
        onCancel={() => {
          setIsFormModalOpen(false);
          resetForm();
        }}
        footer={null}
      >
        <Form id="mail-token-form" form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            label="電子信箱"
            name="email"
            rules={[
              { required: true, message: "請輸入電子信箱" },
              { type: "email", message: "請輸入有效的電子信箱格式" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Token" name="token" rules={[{ required: true, message: "請輸入應用程式專用密碼" }]}>
            <Input.Password />
          </Form.Item>

          <Form.Item label="IMAP 伺服器" name="imap" initialValue="imap.gmail.com" rules={[{ required: true, message: "請選擇 IMAP 伺服器" }]}>
            <Select options={IMAP_OPTIONS} />
          </Form.Item>

          <Form.Item label="啟用狀態" name="is_active" initialValue={true}>
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }} htmlType="submit">
                {editMailToken ? "更新" : "新增"}
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setIsFormModalOpen(false);
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MailTokenModal;
